import React, { useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, ExternalLink, MoreVertical, MoreHorizontal } from 'lucide-react';

interface InAppBrowserModalProps {
  onClose: () => void;
}

const InAppBrowserModal: React.FC<InAppBrowserModalProps> = ({ onClose }) => {
  const [animatingOut, setAnimatingOut] = useState(false);
  const backdropRef = useRef<HTMLDivElement>(null);

  const handleClose = useCallback(() => {
    setAnimatingOut(true);
    setTimeout(() => {
      onClose();
    }, 300);
  }, [onClose]);

  const handleBackdropClick = useCallback((e: React.MouseEvent) => {
    if (e.target === backdropRef.current) {
      handleClose();
    }
  }, [handleClose]);

  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  const content = (
    <>
      {/* Backdrop */}
      <div
        ref={backdropRef}
        onClick={handleBackdropClick}
        className={`fixed inset-0 z-[9999] bg-brand-black/50 backdrop-blur-sm transition-opacity duration-300 ${
          animatingOut ? 'opacity-0' : 'opacity-100'
        } ${isMobile ? '' : 'flex items-center justify-center'}`}
      />

      {/* Sheet / Modal */}
      {isMobile ? (
        <div
          className={`fixed bottom-0 left-0 right-0 z-[10000] transition-transform duration-300 ease-out ${
            animatingOut ? 'translate-y-full' : 'translate-y-0'
          }`}
        >
          <div
            className="bg-white rounded-t-lg overflow-hidden"
            style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
          >
            {/* Handle bar */}
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-9 h-1 rounded-full bg-brand-black/10" />
            </div>

            <SheetContent onClose={handleClose} />
          </div>
        </div>
      ) : (
        <div
          className={`fixed inset-0 z-[10000] flex items-center justify-center pointer-events-none`}
        >
          <div
            className={`bg-white rounded-lg shadow-xl max-w-[400px] w-[90%] overflow-hidden pointer-events-auto transition-all duration-300 ${
              animatingOut ? 'scale-95 opacity-0' : 'scale-100 opacity-100'
            }`}
          >
            <SheetContent onClose={handleClose} />
          </div>
        </div>
      )}
    </>
  );

  return createPortal(content, document.body);
};

const SheetContent: React.FC<{ onClose: () => void }> = ({ onClose }) => (
  <div>
    {/* Header */}
    <div className="flex items-center justify-between px-5 pb-3 pt-3">
      <div>
        <h2 className="text-[17px] font-semibold text-brand-black">
          Abrí Atlas en tu navegador
        </h2>
        <p className="text-[13px] text-brand-black/50 mt-0.5">
          Las descargas no funcionan en este navegador
        </p>
      </div>
      <button
        onClick={onClose}
        className="w-8 h-8 flex items-center justify-center rounded-full bg-brand-black/[0.06] text-brand-black/40 active:bg-brand-black/10 transition-colors shrink-0 ml-3"
      >
        <X className="w-4 h-4" />
      </button>
    </div>

    {/* Divider */}
    <div className="mx-5 h-px bg-brand-black/[0.06]" />

    {/* Steps */}
    <div className="px-5 py-4 flex flex-col gap-4">
      <div className="flex gap-3 items-start">
        <div className="w-6 h-6 rounded-full bg-brand-primary flex items-center justify-center shrink-0">
          <span className="text-[13px] font-semibold text-white">1</span>
        </div>
        <p className="text-[15px] text-brand-black/80 leading-relaxed">
          Tocá
          <span className="inline-flex items-center gap-1.5 mx-1.5 px-2 py-0.5 bg-brand-black/[0.04] rounded-md border border-brand-black/[0.06] align-middle">
            <MoreVertical className="w-4 h-4 text-brand-black/60" />
          </span>
          o
          <span className="inline-flex items-center gap-1.5 mx-1.5 px-2 py-0.5 bg-brand-black/[0.04] rounded-md border border-brand-black/[0.06] align-middle">
            <MoreHorizontal className="w-4 h-4 text-brand-black/60" />
          </span>
          en la parte superior.
        </p>
      </div>
      <div className="flex gap-3 items-start">
        <div className="w-6 h-6 rounded-full bg-brand-primary flex items-center justify-center shrink-0">
          <span className="text-[13px] font-semibold text-white">2</span>
        </div>
        <p className="text-[15px] text-brand-black/80 leading-relaxed">
          Seleccioná
          <span className="inline-flex items-center gap-1.5 mx-1.5 px-2 py-0.5 bg-brand-black/[0.04] rounded-md border border-brand-black/[0.06] align-middle">
            <ExternalLink className="w-3.5 h-3.5 text-brand-black/60" />
            <span className="text-[13px] font-medium text-brand-black/80">Abrir en navegador</span>
          </span>
        </p>
      </div>
    </div>

    {/* Blurb */}
    <div className="mx-5 mb-5 px-3 py-2.5 bg-brand-primary/[0.04] rounded-md border border-brand-primary/10">
      <p className="text-[13px] text-brand-black/50 leading-relaxed">
        Para descargar imágenes y videos, necesitás usar Atlas desde Safari, Chrome u otro navegador. Los navegadores integrados de apps como Instagram o Facebook no permiten descargas.
      </p>
    </div>
  </div>
);

export default InAppBrowserModal;
